import { useEffect, useState } from "react";
import api, { fmtMXN } from "../lib/api";
import Topbar from "../components/Topbar";
import { Building2, Award, Tags, Layers } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const COLORS = ["#FFDF00", "#1A1A1A", "#FF6B6B", "#4ECDC4", "#95E1D3", "#FFA07A"];

export default function Reports() {
  const { t } = useTranslation();
  const [spendByBranch, setSpendByBranch] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [spendByCategory, setSpendByCategory] = useState([]);
  const [spendByCC, setSpendByCC] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get("/dashboard/corporate"),
      api.get("/reports/spend-by-supplier"),
      api.get("/reports/spend-by-category"),
      api.get("/reports/spend-by-cost-center"),
    ]).then(([dashRes, supRes, catRes, ccRes]) => {
      if (dashRes.status === "fulfilled") {
        setSpendByBranch(dashRes.value.data?.spend_by_branch || []);
      }
      if (supRes.status === "fulfilled") {
        setTopSuppliers(Array.isArray(supRes.value.data) ? supRes.value.data : []);
      }
      if (catRes.status === "fulfilled") {
        setSpendByCategory(Array.isArray(catRes.value.data) ? catRes.value.data : []);
      }
      if (ccRes.status === "fulfilled") {
        setSpendByCC(Array.isArray(ccRes.value.data) ? ccRes.value.data : []);
      }
      setLoaded(true);
    });
  }, []);

  if (!loaded) return <><Topbar title={t("reports.title")} /></>;

  return (
    <>
      <Topbar title={t("reports.title")} subtitle={t("reports.subtitle")} />
      <div className="p-6 space-y-5">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#FFDF00] flex items-center justify-center"><Building2 className="w-4.5 h-4.5 text-[#1A1A1A]" /></div>
            <div>
              <h2 className="font-display font-bold text-lg">{t("reports.spendByBranch")}</h2>
              <p className="text-xs text-slate-500">{t("reports.totalPaidAccum")}</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spendByBranch}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="branch" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", borderRadius: 12, color: "white" }}
                  formatter={(v) => fmtMXN(v)} />
                <Bar dataKey="spend" fill="#FFDF00" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center"><Award className="w-4.5 h-4.5 text-amber-700" /></div>
              <div>
                <h2 className="font-display font-bold text-lg">{t("reports.topSuppliers")}</h2>
                <p className="text-xs text-slate-500">{t("reports.byPaidAmount")}</p>
              </div>
            </div>
            {topSuppliers.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">{t("reports.noData") || "Sin datos"}</p>
            ) : (
              <div className="space-y-2.5">
                {topSuppliers.slice(0, 5).map((s, idx) => (
                  <div key={s.supplier || idx} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700">{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{s.supplier || s.business_name || "—"}</div>
                    </div>
                    <div className="text-sm font-bold font-mono">{fmtMXN(s.spend ?? s.total ?? 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center"><Tags className="w-4.5 h-4.5 text-blue-700" /></div>
              <div>
                <h2 className="font-display font-bold text-lg">{t("reports.spendByCategory")}</h2>
                <p className="text-xs text-slate-500">{t("reports.allApprovedPos")}</p>
              </div>
            </div>
            {spendByCategory.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">{t("reports.noData") || "Sin datos"}</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={spendByCategory} dataKey="spend" nameKey="category" cx="50%" cy="50%" outerRadius={70} label={(e) => e.category}>
                      {spendByCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMXN(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="report-spend-by-cost-center">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center"><Layers className="w-4.5 h-4.5 text-indigo-700" /></div>
            <div>
              <h2 className="font-display font-bold text-lg">{t("reports.spendByCostCenter")}</h2>
              <p className="text-xs text-slate-500">{t("reports.allApprovedPos")}</p>
            </div>
          </div>
          {spendByCC.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">{t("reports.noData") || "Sin datos"}</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={spendByCC}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="cost_center_name" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", borderRadius: 12, color: "white" }} formatter={(v) => fmtMXN(v)} />
                  <Bar dataKey="spend" fill="#6366F1" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
}
